import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { map, of, switchMap } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { User } from '../entities/user.js';
import { LecturerDataview } from '../use-case-entities/LecturerDataview.js';

export const subscribeLecturerDataview = registerSubscription({
  operationName: 'subscribeLecturerDataview',
  type: () => [LecturerDataview],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],

  resolve: (skip: Int, limit: Int, filter: BHFilter) => (gqlFields: BaseQLSelectionSet<LecturerDataview>) => {
    if (!filter.institutionID) return of([]);

    return getCollectionHandle(Einzelberatung._collectionName) // TODO wtf?
      .watchQuery({ _class: User._class })
      .pipe(
        switchMap(async () => {
          let pF = await publishFilter(User._class);
          if (context.serverContext) pF = {};
          if (!pF) throw new Error(`Missing publishFilter for ${User._class}`);

          const matchLectures = {
            // we check if the user has the role 'lecturer' and whether the role contains the current institutionID in the contexts
            roles: {
              $elemMatch: {
                name: 'lecturer',
                contexts: { $in: [filter.institutionID] },
              },
            },
          };

          const groupProducts = {
            _id: '$_id',
            user: { $first: '$$ROOT' },
            products: { $push: '$participations.product' },
          };

          const aggregation = [
            { $match: pF },
            { $match: matchLectures },
            { $group: groupProducts },
            { $set: { institutionID: filter.institutionID } }, // make mongo filter match
            { $match: filter.getMongoFilter() },
            { $addFields: { _class: LecturerDataview._class } },
            { $project: LecturerDataview.neededGqlFields },
            { $sort: filter.getMongoOptions().sort },
            { $skip: skip },
            { $limit: limit },
          ];

          const collection = await getRawCollection<User>(User._collectionName);
          const result = await collection.aggregate<LecturerDataview>(aggregation).toArray();
          return result;
        }),
      );
  },
});

/* Calculate the total quantity of all lecturers of an institution */
export const subscribeLecturerDataviewCount = registerSubscription({
  operationName: 'subscribeLecturerDataviewCount',
  type: () => Int,
  params: [{ name: 'filter', type: () => BHFilter }],

  resolve: (filter: BHFilter) => () => {
    return subscribeLecturerDataview(0, 99999999, filter)({ _id: 1 }).pipe(map(items => items.length));
  },
});
